import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import type { Tag } from "@/lib/types";
import { formatInt } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useTagCategories } from "../contacts/hooks";
import { WhatUsesThis, referenceSummary } from "../automations/WhatUsesThis";
import { LoadError } from "@/components/LoadError";

/** Tags with member counts: rename, move between categories, mark "no marketing", archive, create. */
export function TagsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const [cat, setCat] = useState<string>("");
  const [uses, setUses] = useState<Tag | null>(null);
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<Tag | "new" | null>(null);
  const [newCat, setNewCat] = useState(false);
  const tags = useQuery({
    queryKey: ["tags-admin", archived],
    queryFn: () => api<{ tags: Tag[] }>(`/api/v1/tags?with_counts=true${archived ? "&include_archived=true" : ""}`),
  });
  const cats = useTagCategories();
  const update = useMutation({
    mutationFn: ({ id, ...patch }: { id: number; skip_marketing?: boolean; archived?: boolean }) => api(`/api/v1/tags/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tags-admin"] });
      void qc.invalidateQueries({ queryKey: ["tags"] });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const rows = useMemo(() => {
    const t = text.trim().toLowerCase();
    return (tags.data?.tags ?? []).filter((x) => (!t || x.name.toLowerCase().includes(t) || String(x.id) === t) && (!cat || (x.category_name ?? "") === cat));
  }, [tags.data, text, cat]);
  return (
    <Card className="overflow-x-auto">
      <CardHeader className="flex-wrap">
        <CardTitle>Tags</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Input className="h-8 w-48" value={text} onChange={(e) => setText(e.target.value)} placeholder="Find a tag or ID…" aria-label="Find a tag" />
          <Select className="w-auto" value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
            <option value="">Every category</option>
            {(cats.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
            <Checkbox checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Show archived
          </label>
          <Button size="sm" variant="outline" onClick={() => setNewCat(true)}>
            New category
          </Button>
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus /> New tag
          </Button>
        </div>
      </CardHeader>
      <p className="px-4 py-2 text-sm text-[var(--color-muted-foreground)]">
        Tags marked “no marketing” (like Opt-Out 923) leave people out of marketing email by default. Archived tags can't be added any more; people who have
        them keep them. Imported Keap tags keep their Keap ID.
      </p>
      {tags.isLoading && <Skeleton className="m-3 h-40" />}
      {tags.error && <LoadError what="tags" error={tags.error} onRetry={() => tags.refetch()} />}
      {!tags.isLoading && !tags.error && rows.length === 0 && <Empty title="No tags match." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>ID</TH>
              <TH>Tag</TH>
              <TH>Category</TH>
              <TH>Brand</TH>
              <TH className="text-right">People</TH>
              <TH>No marketing</TH>
              <TH>Archived</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {rows.map((t) => (
              <TR key={t.id} className={t.archived_at ? "row-dim" : ""}>
                <TD className="num text-[var(--color-muted-foreground)]">{t.id}</TD>
                <TD className="max-w-[18rem]">
                  <div className="truncate font-medium">{t.name}</div>
                  {t.description && <div className="truncate text-xs text-[var(--color-muted-foreground)]">{t.description}</div>}
                </TD>
                <TD>{t.category_name ?? "—"}</TD>
                <TD>{t.brand_id ? <BrandMark id={t.brand_id} /> : "—"}</TD>
                <TD className="num text-right">
                  {t.members ? (
                    <Link to="/contacts" search={{ tag_id: t.id }} className="text-[var(--color-primary)] hover:underline">
                      {formatInt(t.members)}
                    </Link>
                  ) : (
                    "0"
                  )}
                </TD>
                <TD>
                  <Checkbox aria-label={`No marketing for ${t.name}`} checked={t.skip_marketing} onChange={(e) => update.mutate({ id: t.id, skip_marketing: e.target.checked })} />
                </TD>
                <TD>
                  <Checkbox
                    aria-label={`Archive ${t.name}`}
                    checked={!!t.archived_at}
                    onChange={async (e) => {
                      const archive = e.target.checked;
                      if (!archive) return update.mutate({ id: t.id, archived: false });
                      const used = await referenceSummary("tag", t.id);
                      if (window.confirm(`Archive "${t.name}"? ${used ? used + " " : ""}It can't be added to anyone afterwards (people keep it). Automations that add it will fail until it's restored.`))
                        update.mutate({ id: t.id, archived: true });
                    }}
                  />
                </TD>
                <TD className="whitespace-nowrap text-right">
                  <Button size="sm" variant="ghost" onClick={() => setUses(t)}>
                    What uses this?
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>
                    Edit
                  </Button>
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {editing && <TagDialog tag={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      {uses && <WhatUsesThis kind="tag" idOrKey={uses.id} onClose={() => setUses(null)} />}
      {newCat && <CategoryDialog onClose={() => setNewCat(false)} />}
    </Card>
  );
}

function TagDialog({ tag, onClose }: { tag: Tag | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useTagCategories();
  const [name, setName] = useState(tag?.name ?? "");
  const [desc, setDesc] = useState(tag?.description ?? "");
  const [category, setCategory] = useState<number | "">(tag?.category_id ?? "");
  const [skip, setSkip] = useState(tag?.skip_marketing ?? false);
  const save = useMutation({
    mutationFn: () =>
      tag
        ? api(`/api/v1/tags/${tag.id}`, { method: "PATCH", json: { name, description: desc, category_id: category === "" ? 0 : category, skip_marketing: skip } })
        : api("/api/v1/tags", { json: { name, description: desc || undefined, category_id: category || undefined, skip_marketing: skip } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tags-admin"] });
      void qc.invalidateQueries({ queryKey: ["tags"] });
      toast(tag ? "Tag saved" : `Tag "${name}" created`);
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{tag ? `Edit tag #${tag.id}` : "New tag"}</DialogTitle>
        <DialogDescription>{tag ? "Renaming changes it everywhere it shows; tag history keeps working." : "New tags are numbered from 100,000. Check the list first so there's no duplicate."}</DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="tag-name">Name</Label>
            <Input id="tag-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tag-desc">Description (optional)</Label>
            <Input id="tag-desc" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tag-cat">Category</Label>
            <Select id="tag-cat" value={category} onChange={(e) => setCategory(e.target.value ? Number(e.target.value) : "")}>
              <option value="">None</option>
              {(cats.data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={skip} onChange={(e) => setSkip(e.target.checked)} /> No marketing: leave people with this tag out of marketing email
          </label>
          {save.error && <p className="text-sm text-[var(--color-destructive)]">{save.error.message}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {tag ? "Save tag" : "Create tag"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const save = useMutation({
    mutationFn: () => api("/api/v1/tag-categories", { json: { name } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tag-categories"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>New tag category</DialogTitle>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Events" aria-label="Category name" required />
          {save.error && <p className="text-sm text-[var(--color-destructive)]">{save.error.message}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              Create category
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

